<template>
  <Teleport to="body">
  <div v-if="show" class="fixed inset-0 z-[300] flex items-center justify-center">
    <div class="absolute inset-0 bg-black opacity-50 modal-wrapper" @click="attemptClose"></div>
    <div
      class="relative bg-white rounded-lg shadow-lg w-full max-w-6xl md:max-w-4xl p-4 md:p-6 z-10 m-2 md:m-4 max-h-[95vh] md:max-h-[90vh] overflow-y-auto">
      <!-- Header -->
      <div class="flex justify-between items-center mb-6">
        <div>
          <h2 class="text-2xl font-bold text-gray-900">Edit Recipe</h2>
        </div>
        <button @click="attemptClose" class="text-gray-600 hover:text-gray-800 transition-colors">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <form @submit.prevent="submitEdit">
        <!-- Recipe Title -->
        <div class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700">Recipe Title *</label>
            <input v-model="formData.title" type="text" placeholder="Enter recipe title..."
              class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500" />
          </div>

          <!-- Description -->
          <div>
            <label class="block text-sm font-medium text-gray-700">Description *</label>
            <textarea v-model="formData.description" rows="4" placeholder="Describe your recipe..."
              class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"></textarea>
          </div>

          <!-- Instructions -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">Instructions</label>
            <RichTextEditor v-model="formData.content" />
          </div>

          <!-- Category -->
          <div>
            <label class="block text-sm font-medium text-gray-700">Category *</label>
            <select v-model="formData.category"
              class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500">
              <option value="">Select a category</option>
              <option v-for="category in categories" :key="category.id" :value="category.name">
                {{ category.name }}
              </option>
            </select>
          </div>

          <!-- Ingredients -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-4">Ingredients *</label>

            <!-- Add Ingredient Search -->
            <div class="relative mb-6">
              <div class="flex items-center justify-between mb-2">
                <label class="block text-sm font-medium text-gray-700">Add Ingredients</label>
                <button @click="toggleIngredientMode" type="button"
                  class="text-sm text-green-600 hover:text-green-700 font-medium transition-colors">
                  {{ isCreatingNewIngredient ? 'Search existing' : 'Create new' }}
                </button>
              </div>

              <!-- Search existing ingredients -->
              <div v-if="!isCreatingNewIngredient">
                <input v-model="searchIngredient" type="text" placeholder="Search ingredients..."
                  class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  @focus="handleIngredientSearchFocus" @blur="handleIngredientSearchBlur"
                  @keydown.enter.prevent="addIngredientFromSearch" />

                <!-- Ingredient Dropdown -->
                <div v-if="showIngredientDropdown && availableIngredients.length > 0"
                  class="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  <button v-for="ingredient in availableIngredients.slice(0, 10)" :key="ingredient.id"
                    @click="addIngredient(ingredient)" type="button"
                    class="w-full px-4 py-2 text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none">
                    {{ ingredient.name }}
                  </button>
                </div>

                <!-- Show create option if no matches -->
                <div v-if="showIngredientDropdown && availableIngredients.length === 0 && searchIngredient.trim()"
                  class="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
                  <button @click="addNewIngredientFromSearch" type="button"
                    class="w-full px-4 py-2 text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none text-green-600">
                    Create "{{ searchIngredient.trim() }}"
                  </button>
                </div>
              </div>

              <!-- Create new ingredient -->
              <div v-else class="space-y-3">
                <input v-model="searchIngredient" type="text" placeholder="Enter ingredient name..."
                  class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  @keydown.enter.prevent="addNewIngredientFromSearch" />
                <div class="flex gap-2">
                  <button @click="addNewIngredientFromSearch" :disabled="!searchIngredient.trim()" type="button"
                    class="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                    Add Ingredient
                  </button>
                </div>
              </div>

              <p v-if="loadingIngredients && !isCreatingNewIngredient" class="text-gray-500 text-sm mt-1">Loading
                ingredients...</p>
            </div>

            <div class="space-y-4">
              <div v-for="(ingredient, index) in formData.ingredients" :key="ingredient.id || index"
                class="bg-gray-50 rounded-lg p-4 space-y-3">
                <!-- Ingredient Name -->
                <div class="flex items-center justify-between">
                  <div class="flex-1 mr-3">
                    <label class="block text-sm font-medium text-gray-700 mb-1">Ingredient Name</label>
                    <input v-model="ingredient.name" type="text" placeholder="e.g., Tomatoes"
                      class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-base" />
                  </div>
                  <button type="button" @click="removeIngredient(index)"
                    class="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                    title="Remove ingredient">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24"
                      stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>

                <!-- Quantity and Unit -->
                <div class="grid grid-cols-2 gap-3">
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">Quantity</label>
                    <input v-model="ingredient.quantity" type="number" step="0.1" min="0" placeholder="e.g., 2"
                      class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-base" />
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">Unit</label>
                    <input v-model="ingredient.unit" type="text" placeholder="e.g., cups, tsp"
                      class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-base" />
                  </div>
                </div>
              </div>
            </div>

            <button type="button" @click="addNewIngredient"
              class="mt-4 w-full sm:w-auto px-6 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors flex items-center justify-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24"
                stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
              </svg>
              Add new Ingredient
            </button>
          </div>

          <!-- Submit Button -->
          <div class="flex justify-end mt-6">
            <button type="submit" class="px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600">
              Save Changes
            </button>
          </div>
        </div>
      </form>
    </div>
  </div>

  <!-- Unsaved Changes Dialog -->
  <UnsavedChangesDialog
    :show="showUnsavedChangesDialog"
    @keep-editing="keepEditing"
    @discard="discardChanges"
  />
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useMutation, useQuery } from '@vue/apollo-composable'
import { UPDATE_RECIPE } from '../graphql/mutations'
import { GET_ALL_RECIPE_CATEGORIES, GET_ALL_INGREDIENTS } from '../graphql/queries'
import { showError, showSuccess } from '../utils/errorHandling'
import UnsavedChangesDialog from './UnsavedChangesDialog.vue'
import RichTextEditor from './RichTextEditor.vue'
import { useBodyScrollLock } from '../composables/useBodyScrollLock'

// Types
interface Ingredient {
  id?: string
  name: string
  quantity: number
  unit: string
}

interface FormData {
  id: string
  title: string
  description: string
  content: string
  category: string
  ingredients: Ingredient[]
}

// Props
interface Props {
  show: boolean
  recipe: any
}

const props = defineProps<Props>()
useBodyScrollLock(() => props.show)

// Emits
const emit = defineEmits(['close', 'updated'])

// State
const formData = ref<FormData>({
  id: '',
  title: '',
  description: '',
  content: '',
  category: '',
  ingredients: [],
})
const originalFormData = ref<FormData>({
  id: '',
  title: '',
  description: '',
  content: '',
  category: '',
  ingredients: [],
})
const searchIngredient = ref('')
const showIngredientDropdown = ref(false)
const isCreatingNewIngredient = ref(false)
const showUnsavedChangesDialog = ref(false)
const pendingClose = ref(false)

const { result: categoriesResult } = useQuery(GET_ALL_RECIPE_CATEGORIES)
const { result: ingredientsResult, loading: loadingIngredients, refetch: refetchIngredients } = useQuery(GET_ALL_INGREDIENTS)
const { mutate: updateRecipe } = useMutation(UPDATE_RECIPE, {
  update: (cache, { data }) => {
    // Update the GET_ALL_INGREDIENTS cache with any new ingredients created during recipe update
    if (data?.updateRecipe?.ingredients) {
      try {
        const existingData = cache.readQuery({ query: GET_ALL_INGREDIENTS })
        if (existingData) {
          // Get all ingredients from the updated recipe
          const recipeIngredients = data.updateRecipe.ingredients

          // Find which ingredients are new (not in the existing cache)
          const newIngredients = recipeIngredients.filter((ing: any) => {
            return ing.id && !(existingData as any).getAllIngredients?.some((existing: any) =>
              existing.id === ing.id
            )
          })

          // If there are new ingredients, add them to the cache
          if (newIngredients.length > 0) {
            cache.writeQuery({
              query: GET_ALL_INGREDIENTS,
              data: {
                getAllIngredients: [...(existingData as any).getAllIngredients || [], ...newIngredients]
              }
            })
          }
        }
      } catch (error) {
        console.error('Error updating ingredients cache:', error)
      }
    }
  },
  refetchQueries: [{ query: GET_ALL_INGREDIENTS }]
})

// Computed
const categories = computed(() => categoriesResult.value?.getAllRecipeCategories || [])
const allIngredients = computed(() => ingredientsResult.value?.getAllIngredients || [])

const filteredIngredients = computed(() => {
  if (!searchIngredient.value) return allIngredients.value

  const search = searchIngredient.value.toLowerCase()
  return allIngredients.value.filter((ingredient: any) =>
    ingredient.name.toLowerCase().includes(search)
  )
})

const availableIngredients = computed(() => {
  const usedIngredientNames = formData.value.ingredients.map(ing => ing.name.toLowerCase())
  return filteredIngredients.value.filter((ingredient: any) =>
    !usedIngredientNames.includes(ingredient.name.toLowerCase())
  )
})

const hasUnsavedChanges = computed(() => {
  // Compare current form data with original data
  if (formData.value.title !== originalFormData.value.title ||
      formData.value.description !== originalFormData.value.description ||
      formData.value.content !== originalFormData.value.content ||
      formData.value.category !== originalFormData.value.category) {
    return true
  }
  
  // Compare ingredients
  if (formData.value.ingredients.length !== originalFormData.value.ingredients.length) {
    return true
  }
  
  return formData.value.ingredients.some((ing, index) => {
    const original = originalFormData.value.ingredients[index]
    return ing.name !== original.name ||
           ing.quantity !== original.quantity ||
           ing.unit !== original.unit
  })
})

// Methods
const attemptClose = () => {
  if (hasUnsavedChanges.value) {
    pendingClose.value = true
    showUnsavedChangesDialog.value = true
  } else {
    closeModal()
  }
}

const closeModal = () => {
  showUnsavedChangesDialog.value = false
  pendingClose.value = false
  emit('close')
}

const keepEditing = () => {
  showUnsavedChangesDialog.value = false
  pendingClose.value = false
}

const discardChanges = () => {
  closeModal()
}

const addIngredient = (ingredient: any) => {
  formData.value.ingredients.push({
    id: ingredient.id,
    name: ingredient.name,
    quantity: 0,
    unit: ''
  })
  searchIngredient.value = ''
  showIngredientDropdown.value = false
}

const addNewIngredientFromSearch = () => {
  const ingredientName = searchIngredient.value.trim()
  if (!ingredientName) return

  // Check if ingredient already exists in the list
  const exists = formData.value.ingredients.some(ing =>
    ing.name.toLowerCase() === ingredientName.toLowerCase()
  )

  if (!exists) {
    formData.value.ingredients.push({
      name: ingredientName,
      quantity: 0,
      unit: ''
    })
  }

  searchIngredient.value = ''
  showIngredientDropdown.value = false
  isCreatingNewIngredient.value = false
}

const addNewIngredient = () => {
  formData.value.ingredients.push({ name: '', quantity: 0, unit: '' })
}

const removeIngredient = (index: number) => {
  formData.value.ingredients.splice(index, 1)
}

const handleIngredientSearchFocus = () => {
  showIngredientDropdown.value = true
}

const handleIngredientSearchBlur = () => {
  // Delay hiding to allow clicking on dropdown items
  setTimeout(() => {
    showIngredientDropdown.value = false
  }, 200)
}

const toggleIngredientMode = () => {
  isCreatingNewIngredient.value = !isCreatingNewIngredient.value
  searchIngredient.value = ''
  showIngredientDropdown.value = false
}

const addIngredientFromSearch = () => {
  if (availableIngredients.value.length > 0) {
    addIngredient(availableIngredients.value[0])
  } else if (searchIngredient.value.trim()) {
    addNewIngredientFromSearch()
  }
}

const submitEdit = async () => {
  try {
    await updateRecipe({
      id: formData.value.id,
      title: formData.value.title,
      description: formData.value.description,
      content: formData.value.content || null,
      category: formData.value.category,
      ingredients: formData.value.ingredients.map((ing: Ingredient) => ({
        name: ing.name,
        quantity: ing.quantity,
        unit: ing.unit
      }))
    })

    emit('updated', formData.value)
    closeModal()
    showSuccess(`Recipe "${formData.value.title}" updated successfully!`)
  } catch (error) {
    showError(error, 'update recipe')
  }
}

// Watch
watch(() => props.recipe, (newRecipe) => {
  if (newRecipe && typeof newRecipe === 'object') {
    // Process ingredients with proper fallbacks
    let processedIngredients: Ingredient[] = []
    if (Array.isArray(newRecipe.ingredients)) {
      processedIngredients = newRecipe.ingredients.map((ing: any) => {
        if (ing && typeof ing === 'object') {
          return {
            id: ing.id || '',
            name: ing.name || '',
            quantity: Number(ing.quantity) || 0,
            unit: ing.unit || ''
          }
        }
        return { id: '', name: '', quantity: 0, unit: '' }
      })
    }

    const recipeData = {
      id: newRecipe.id || '',
      title: newRecipe.title || '',
      description: newRecipe.description || '',
      content: newRecipe.content || '',
      category: newRecipe.category?.name || '',
      ingredients: processedIngredients,
    }
    
    formData.value = { ...recipeData }
    originalFormData.value = { 
      ...recipeData,
      ingredients: processedIngredients.map(ing => ({ ...ing }))
    }
  }
}, { immediate: true })

// Watch for modal visibility changes to refetch ingredients
watch(() => props.show, async (show) => {
  if (show) {
    // Refetch ingredients when modal opens to get the latest data
    await refetchIngredients()
  }
})
</script>
